import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useUser } from '@clerk/clerk-react';
import {
  CONSENT_CATEGORIES, adsApply, consentSignals, readConsent, writeConsent,
} from '../config/consent';
import { trackingEnabled } from '../config/tracking';

// Consent state shared by the whole app: the banner, the footer entry
// point, the tracking gate and the restricted mode.
const ConsentContext = createContext(null);

export const useConsent = () => {
  const value = useContext(ConsentContext);
  if (!value) throw new Error('useConsent must be used inside ConsentProvider');
  return value;
};

// Pushes the current choice to Google Consent Mode (so the GTM container
// only fires the tags the student allowed) and to the data layer.
const applyConsent = (consent) => {
  const signals = consentSignals(consent);
  if (!trackingEnabled) return signals;
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag('consent', 'update', signals);
  }
  return signals;
};

const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(() => readConsent());
  const [panelOpen, setPanelOpen] = useState(false);
  const { user } = useUser();
  const plan = user?.publicMetadata?.plan || null;
  // The ads only exist on the free plan: a paying student never gets them,
  // so they are never granted for them.
  const adsAllowed = adsApply(plan);
  const consentRef = useRef(consent);
  consentRef.current = consent;

  // The choice is applied as soon as it is known, so no tag fires before
  // the student has answered.
  useEffect(() => {
    applyConsent(consent);
  }, [consent]);

  const update = useCallback((patch) => {
    // The ref carries the latest value, so two quick updates (accept all
    // then save) never build on a stale consent.
    const next = writeConsent({ ...consentRef.current, ...patch });
    consentRef.current = next;
    setConsent(next);
  }, []);

  const acceptAll = useCallback(() => {
    update({
      analytics: trackingEnabled,
      ads: trackingEnabled && adsAllowed,
      preferences: true,
      restricted: false,
    });
    setPanelOpen(false);
  }, [update, adsAllowed]);

  // Refusing the optional cookies: the site keeps working on the
  // necessary ones only. The student can still restrict the app to the
  // free resources from the panel.
  const rejectAll = useCallback(() => {
    update({ analytics: false, ads: false, preferences: false });
  }, [update]);

  const save = useCallback((choices) => {
    update({
      analytics: trackingEnabled && !!choices.analytics,
      ads: trackingEnabled && adsAllowed && !!choices.ads,
      preferences: !!choices.preferences,
      restricted: !!choices.restricted,
    });
    setPanelOpen(false);
  }, [update, adsAllowed]);

  // "Only the free resources": the app is limited to the public
  // resources, the chat and the account stay out of reach.
  const restrict = useCallback(() => {
    update({ analytics: false, ads: false, preferences: false, restricted: true });
    setPanelOpen(false);
  }, [update]);

  const openPanel = useCallback(() => setPanelOpen(true), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);

  const value = useMemo(() => {
    const decided = !!consent.decidedAt;
    return {
      consent,
      decided,
      // The banner only asks once; the panel is always reachable.
      showBanner: !decided,
      panelOpen,
      openPanel,
      closePanel,
      acceptAll,
      rejectAll,
      save,
      restrict,
      update,
      // The ads only exist on the free plan.
      adsAllowed,
      // False on localhost/development: GTM and the optional categories are off.
      trackingEnabled,
      categories: CONSENT_CATEGORIES,
    };
  }, [
    consent, panelOpen, openPanel, closePanel, acceptAll, rejectAll, save,
    restrict, update, adsAllowed,
  ]);

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
};

export default ConsentProvider;
